import { useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';

type Edition = 'admin' | 'client' | 'employee';

interface GuideSection {
  title: string;
  icon: string;
  items: { q: string; a: string }[];
}

const ADMIN_GUIDE: GuideSection[] = [
  {
    title: 'Getting Started',
    icon: 'rocket_launch',
    items: [
      { q: 'Setting up your company profile', a: 'Go to Settings → General to fill in your company name, logo, address, and contact information. This information appears on quotes and invoices sent to clients.' },
      { q: 'Adding your first job', a: 'From the Jobs page, click "New Job". Fill in the job title, client, location, start/end dates, and assign a crew. Jobs are the central hub for all project activity.' },
      { q: 'Inviting employees and clients to their portals', a: 'On any employee or client detail page, click the "Invite" or "Invite to Portal" button. This generates a secure link they use to register a portal account.' },
    ],
  },
  {
    title: 'Jobs & Scheduling',
    icon: 'construction',
    items: [
      { q: 'Creating and managing jobs', a: 'Jobs track all work for a client. Each job has tasks, documents, invoices, and schedule entries. Use the status workflow: Draft → Active → In Progress → Completed.' },
      { q: 'Scheduling crew on the calendar', a: 'Use the Calendar view to drag-and-drop schedule entries. Assign jobs to crews and set start/end times. The calendar shows daily, weekly, and monthly views.' },
      { q: 'Job tasks and subtasks', a: 'Inside a job, use the Tasks tab to break work into trackable steps. Each task can have subtasks, assigned employees, due dates, and status tracking.' },
      { q: 'Managing change orders', a: 'Change orders are created from within a job. They can be sent to clients for approval via a secure link. Once approved, the job value updates automatically.' },
      { q: 'Docs & Photos on a job', a: 'Every job has a "Docs & Photos" section (right under Overview in the section tree). Drag-and-drop files, set per-file visibility for workers and the client, flag documents for specific workers to read, and rename files with working names.' },
      { q: 'Scanning documents with your phone', a: 'The "Scan Document" button in a job\'s Docs & Photos opens your phone or tablet camera (a file picker on desktop) — snap the page and it uploads straight into the job\'s documents. Works in every browser.' },
    ],
  },
  {
    title: 'Clients',
    icon: 'groups',
    items: [
      { q: 'Adding a new client', a: 'Go to Clients and click "New Client". Enter name, company, email, phone, and address. Clients are linked to all their jobs, quotes, and invoices.' },
      { q: 'Viewing client history', a: 'The client detail page shows all jobs, quotes, material estimates, labour estimates, and invoices for that client in one place.' },
      { q: 'Sending a client their portal invite', a: 'On the client detail page, click "Invite to Portal". This generates a single-use secure link. The client uses it to create their account and view job updates, documents, and invoices.' },
      { q: 'Syncing to QuickBooks', a: 'If QuickBooks is connected in Settings → Integrations, the "Sync to QB" button on the client page exports the client to QuickBooks Online.' },
    ],
  },
  {
    title: 'Quotes & Estimates',
    icon: 'request_quote',
    items: [
      { q: 'Building a quote', a: 'Go to Quotes → New Quote, or create one from a client or job page. The Quote Builder lets you add sections, line items, quantities, prices, and tax rates. Save as Draft or Send directly to the client.' },
      { q: 'Material vs. Labour estimates', a: 'Material estimates list products with quantities and unit prices. Labour estimates track hours and hourly rates. Both can be converted into formal quotes.' },
      { q: 'Quote statuses', a: 'Draft → Sent → Approved or Declined. Once approved, convert the quote to an invoice with one click. Quote history is stored for reference.' },
      { q: 'Creating an invoice from a quote', a: 'On the Quotes page or client detail, use "Create Invoice from Quote" to auto-populate an invoice with all quote line items.' },
    ],
  },
  {
    title: 'Invoices & Payments',
    icon: 'receipt_long',
    items: [
      { q: 'Creating an invoice', a: 'Go to Invoices → New Invoice, or create from a job/client. Add line items, set due date, and send. Invoices track outstanding A/R on the dashboard.' },
      { q: 'Recording a payment', a: 'On the invoice detail, click "Record Payment" and enter the amount, date, and payment method. Partial payments are supported — the invoice tracks the balance automatically.' },
      { q: 'Online card payments (Stripe)', a: 'Clients can pay outstanding invoices by credit card from their portal ("Pay Now"). Card payments record themselves against the invoice and email the client a receipt. Currently in test mode — add live Stripe keys to accept real cards.' },
      { q: 'Invoice statuses', a: 'Draft → Sent → Partially Paid → Paid or Overdue. Overdue invoices appear on the dashboard as alerts.' },
      { q: 'Progress / draw billing', a: 'On a job\'s Invoices section, the "Draw billing schedule" panel bills the project in stages: use the standard residential schedule (deposit → foundation → framing → rough-ins → completion → final) or add your own milestones as a % of the contract or fixed amounts. "Generate invoice" turns a milestone into a draft draw invoice (GST included, linked to the job and client) ready to review and send; each milestone shows Pending → Invoiced → Paid as it progresses.' },
      { q: 'Attaching files to invoices', a: 'The invoice editor has an Attachments section. Upload photos, PDFs, or documents to include with the invoice. You can also pull from Job Documents.' },
    ],
  },
  {
    title: 'Vendors & Expenses',
    icon: 'store',
    items: [
      { q: 'Vendor profiles', a: 'Click any vendor on the Vendors page to open their profile: total spend, what\'s paid, what\'s pending with expected due dates, and overdue count. The payment history links each expense back to its job.' },
      { q: 'Logging an expense against a vendor', a: 'On a vendor profile, click "Log expense" — pick the job, amount, category, and invoice number, and mark it paid or set the expected due date. It lands in the job\'s expense ledger with the vendor linked.' },
      { q: 'Tracking what you owe', a: 'Unpaid expenses appear in the vendor\'s "Pending payment" list sorted by due date, with overdue ones flagged in red. One tap on "Mark paid" settles them. Receipt scans on the job Expenses tab can also record paid/pending status.' },
    ],
  },
  {
    title: 'Employees & Payroll',
    icon: 'badge',
    items: [
      { q: 'Adding a new employee', a: 'Go to Employees → New Employee. Fill in name, role, crew, pay rate, and contact info. Employees can be workers or subcontractors.' },
      { q: 'Time off and schedule', a: 'Each employee has a Time Off tab for vacation/sick/personal requests. The Schedule tab shows all their assigned jobs and events.' },
      { q: 'Timesheets and approvals', a: 'Go to Timesheets to see employee time entries submitted through the employee portal. Approve or reject entries and export for payroll processing.' },
      { q: 'Adding time for an employee', a: 'On an employee\'s Timesheets tab, the job dropdown pre-selects whatever job they\'re scheduled on for the chosen date (home job or override) — just enter the hours. You can still pick a different job; admin entries are auto-approved.' },
      { q: 'Skills and certifications', a: 'The Skills tab on an employee record tracks certifications, tickets, and licenses with expiry dates. Expired certifications show red alerts.' },
    ],
  },
  {
    title: 'Reports & Finance',
    icon: 'bar_chart',
    items: [
      { q: 'Revenue reports', a: 'The Reports page shows revenue by month, job, and client. Filter by date range to see trends. Export to CSV for accounting.' },
      { q: 'Financial projections', a: 'The Projections page forecasts revenue based on active jobs, pending quotes, and historical patterns. Adjust assumptions to model different scenarios.' },
      { q: 'Dashboard KPIs', a: 'The main dashboard shows Active Jobs, Outstanding A/R, Overdue Invoices, and Pending Estimates at a glance. The Revenue Trend chart shows the last 6 months.' },
      { q: 'Dashboard task lists & daily logs', a: 'The "Job Task Lists" panel shows every active job\'s planner tasks expanded — tap the circle to complete one. The "Daily Log" card lets you file today\'s log for any job in seconds (jobs already logged today get a check mark), and "Needs Attention" lists records with missing data (client emails, job budgets, pay rates) with one-tap fix links.' },
      { q: 'Payroll processing', a: 'Go to Payroll to run pay periods. Enter regular and overtime hours, and the system calculates gross pay, deductions (CPP, EI, income tax), and net pay for each employee.' },
    ],
  },
  {
    title: 'Settings',
    icon: 'settings',
    items: [
      { q: 'Company information', a: 'Settings → General: company name, address, phone, email, logo, and brand colors for documents.' },
      { q: 'Tax rates and invoice terms', a: 'Settings → Finance: set default GST/PST rates, payment terms, and invoice footer text.' },
      { q: 'Dark mode', a: 'Use the moon/sun toggle in the top bar (next to notifications) to switch the whole app between light and dark. It remembers your choice and follows your device setting by default.' },
      { q: 'Integrations', a: 'Settings → Integrations: connect QuickBooks Online, Google Calendar, and other services.' },
      { q: 'User accounts and roles', a: 'Settings → Users: manage admin and staff accounts. Roles: Owner (full access), Admin (most access), Staff (limited).' },
      { q: 'Portal invites', a: 'Settings → Invites: view all pending and used portal invite links. Generate new invites for clients and employees.' },
    ],
  },
  {
    title: 'Mail, Analytics & Tools',
    icon: 'apps',
    items: [
      { q: 'Email (Mail)', a: 'The Mail section brings your ochrepoint.ca inbox into the app, organized address → sender → conversation. Reply or compose with your branded signature template, attach files, search message text, and use "AI draft" to draft a reply from the thread. New contacts can be turned into clients in one tap.' },
      { q: 'Composing with templates & preview', a: 'The Compose window has a template picker (your saved outreach templates, grouped by category), a Cc field, and a Preview button that shows exactly what the recipient will see — your branded header and signature included. Pick which address it sends from, including admin@.' },
      { q: 'Contacts & Attachments in Mail', a: 'The "Contacts" section of the mail rail lists every person, expanding to each of your addresses they\'ve written to (with read/unread counts) — click one to open the full back-and-forth. "Attachments" lists every file ever emailed, grouped by contact and split sent/received; click to open.' },
      { q: 'Site Analytics', a: 'Site Analytics shows ochrepoint.ca traffic — visitors, sessions, top pages — plus referral links you can create (share as ops.ochrepoint.ca/r/your-slug) and a JS error log. Resolving an error also queues it to be fixed.' },
      { q: 'File Explorer', a: 'File Explorer (Resources menu) shows your whole business as a folder desktop: Clients open to Jobs, which open to Invoices / Quotes / Documents. Launcher icons and records open as real windows — run Mail, an invoice, and the planner side by side, drag/resize/pin them, and switch via the taskbar. Your window layout survives a reload; on phones windows open full-screen.' },
      { q: 'Requesting changes to the app', a: 'The flag button (bottom-left of every page) files a change request. Use "Annotate the page" to point at elements, drag a region, or drop markers — a screenshot of your marks is attached automatically so the dev team sees exactly what you mean.' },
    ],
  },
];

const CLIENT_GUIDE: GuideSection[] = [
  {
    title: 'Your Client Portal',
    icon: 'home',
    items: [
      { q: 'Accessing your portal', a: 'You received an invite link from OchrePoint Contracting. Click the link and create your account. Once registered, log in at ochrepoint.ca/portal/login.' },
      { q: 'Your dashboard', a: 'Your home page shows active jobs, upcoming schedule dates, recent documents, and outstanding invoices — everything about your project in one place.' },
    ],
  },
  {
    title: 'Jobs & Schedule',
    icon: 'calendar_month',
    items: [
      { q: 'Viewing your job schedule', a: 'The Schedule tab shows all planned work dates for your project. Dates include the crew assigned and estimated completion times.' },
      { q: 'Job status updates', a: 'Job status is updated by the contractor as work progresses. You can see when work is scheduled, in progress, or completed.' },
    ],
  },
  {
    title: 'Documents',
    icon: 'description',
    items: [
      { q: 'Accessing project documents', a: 'The Documents tab shows all files shared with you — contracts, plans, photos, permits, and more. Click any file to view or download it.' },
      { q: 'Invoice documents', a: 'Invoices may have attachments (photos of completed work, material receipts) visible in the Invoice details.' },
    ],
  },
  {
    title: 'Invoices',
    icon: 'receipt_long',
    items: [
      { q: 'Viewing your invoices', a: 'The Invoices tab shows all invoices issued for your project — amounts, due dates, and payment status.' },
      { q: 'Paying an invoice online', a: 'Open an outstanding invoice and tap "Pay Now" to pay securely by credit card — the balance updates automatically and you get a receipt by email. You can also copy a payment link or download the invoice PDF. Prefer another method? Contact OchrePoint Contracting to arrange it.' },
    ],
  },
  {
    title: 'Notifications',
    icon: 'notifications',
    items: [
      { q: 'Setting up notifications', a: 'Go to Notification Settings in your portal to choose how you receive updates — email, SMS, or in-app — for schedule changes, document uploads, and new invoices.' },
    ],
  },
];

const EMPLOYEE_GUIDE: GuideSection[] = [
  {
    title: 'Your Employee Portal',
    icon: 'home',
    items: [
      { q: 'Accessing your portal', a: 'Your employer sent you an invite link. Click it to create your account. Once set up, log in at ochrepoint.ca/portal/login.' },
      { q: 'Your dashboard', a: 'Your home page shows upcoming scheduled jobs, recent time entries, and any pending actions — like timesheet submissions.' },
    ],
  },
  {
    title: 'Schedule',
    icon: 'calendar_month',
    items: [
      { q: 'Viewing your work schedule', a: 'The Schedule page shows all your upcoming job assignments by date. Job details include location, crew, and any notes from your supervisor.' },
      { q: 'Schedule notifications', a: 'Enable notifications in Settings to get alerts when your schedule changes or a new job is assigned.' },
    ],
  },
  {
    title: 'Timesheets',
    icon: 'schedule',
    items: [
      { q: 'Submitting your hours', a: 'Go to Timesheet and log hours for each day you worked. Enter start/end times or total hours, the job you worked on, and any notes. Submit when complete.' },
      { q: 'Timesheet approval', a: 'Your supervisor reviews and approves submitted timesheets. You will see the approval status in your portal. Approved timesheets are used for payroll.' },
      { q: 'Correcting a timesheet', a: 'If an entry needs correction before approval, you can edit it. Once approved, contact your supervisor to make changes.' },
    ],
  },
  {
    title: 'Documents',
    icon: 'description',
    items: [
      { q: 'Accessing work documents', a: 'The Documents page shows files your employer has shared with you — safety plans, manuals, contracts, and job documents.' },
    ],
  },
  {
    title: 'Notifications',
    icon: 'notifications',
    items: [
      { q: 'Notification preferences', a: 'Go to Notification Settings to choose email or SMS alerts for schedule updates, timesheet reminders, and approval notifications.' },
    ],
  },
];

const GUIDES = { admin: ADMIN_GUIDE, client: CLIENT_GUIDE, employee: EMPLOYEE_GUIDE };

export const HelpCenterPage = () => {
  const [edition, setEdition] = useState<Edition>('admin');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  const guide = GUIDES[edition];

  const toggleSection = (key: string) => setOpenSections(s => ({ ...s, [key]: !s[key] }));
  const toggleItem = (key: string) => setOpenItems(s => ({ ...s, [key]: !s[key] }));

  const filtered = search.trim()
    ? guide.map(sec => ({
        ...sec,
        items: sec.items.filter(
          item =>
            item.q.toLowerCase().includes(search.toLowerCase()) ||
            item.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter(sec => sec.items.length > 0)
    : guide;

  return (
    <MainLayout>
      <div className="flex flex-col min-h-screen">
        <header className="sticky top-14 md:top-0 z-20 border-b border-gray-200 px-4 py-4 md:px-8 md:py-5 bg-gray-50/80 backdrop-blur-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="bg-primary/10 rounded-md p-2">
                <span className="material-symbols-outlined text-primary text-2xl">help</span>
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Help Center</h2>
                <p className="text-sm text-gray-500">User guides for every role</p>
              </div>
            </div>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">search</span>
              <input
                type="text"
                placeholder="Search help articles…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
          </div>
        </header>

        <div className="p-4 md:p-8">
          <div className="max-w-3xl mx-auto">
            {/* Edition tabs */}
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-xl w-fit">
              {(['admin', 'client', 'employee'] as Edition[]).map(ed => (
                <button
                  key={ed}
                  onClick={() => { setEdition(ed); setSearch(''); setOpenSections({}); setOpenItems({}); }}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    edition === ed
                      ? 'bg-white text-primary shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {ed.charAt(0).toUpperCase() + ed.slice(1)} Guide
                </button>
              ))}
            </div>

            {/* Intro blurb */}
            <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-xl">
              {edition === 'admin' && (
                <p className="text-sm text-gray-700"><strong>Admin Guide</strong> — Everything you need to manage OchrePoint Contracting's operations: jobs, clients, team, finance, and settings.</p>
              )}
              {edition === 'client' && (
                <p className="text-sm text-gray-700"><strong>Client Guide</strong> — A reference for clients using the OchrePoint client portal to track their project, view documents, and manage invoices.</p>
              )}
              {edition === 'employee' && (
                <p className="text-sm text-gray-700"><strong>Employee Guide</strong> — For field workers and staff using the employee portal to view schedules, submit timesheets, and access documents.</p>
              )}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <span className="material-symbols-outlined text-5xl mb-3 block">search_off</span>
                No results for "{search}"
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((section, si) => {
                  const secKey = `${edition}-${si}`;
                  const isSecOpen = search ? true : (openSections[secKey] ?? si === 0);
                  return (
                    <div key={si} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => toggleSection(secKey)}
                        className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-primary">{section.icon}</span>
                          <span className="font-semibold text-gray-900">{section.title}</span>
                          <span className="text-xs text-gray-400 font-normal">({section.items.length} articles)</span>
                        </div>
                        <span className={`material-symbols-outlined text-gray-400 transition-transform ${isSecOpen ? 'rotate-180' : ''}`}>expand_more</span>
                      </button>
                      {isSecOpen && (
                        <div className="border-t border-gray-100 divide-y divide-gray-50">
                          {section.items.map((item, ii) => {
                            const itemKey = `${secKey}-${ii}`;
                            const isItemOpen = openItems[itemKey] ?? false;
                            return (
                              <div key={ii}>
                                <button
                                  onClick={() => toggleItem(itemKey)}
                                  className="w-full flex items-start justify-between px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                                >
                                  <span className="text-sm font-medium text-gray-800 pr-4">{item.q}</span>
                                  <span className={`material-symbols-outlined text-gray-400 flex-shrink-0 text-base mt-0.5 transition-transform ${isItemOpen ? 'rotate-180' : ''}`}>expand_more</span>
                                </button>
                                {isItemOpen && (
                                  <div className="px-5 pb-4">
                                    <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-8 p-5 bg-gray-50 rounded-xl border border-gray-200 text-center">
              <p className="text-sm text-gray-600 mb-2">Can't find what you need?</p>
              <p className="text-sm text-gray-500">Contact OchrePoint Contracting at <span className="text-primary font-medium">admin@ochrepoint.ca</span></p>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
